import { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SealCheck, ShieldCheck, WarningCircle } from "@phosphor-icons/react";

export default function SignPage() {
  const { token } = useParams();
  const [doc, setDoc] = useState(null);
  const [error, setError] = useState(null);
  const [step, setStep] = useState("review"); // review | otp | done
  const [signerName, setSignerName] = useState("");
  const [otp, setOtp] = useState("");
  const [hintOtp, setHintOtp] = useState(null);
  const [busy, setBusy] = useState(false);
  const [certId, setCertId] = useState(null);

  const load = useCallback(async () => {
    try { const res = await api.get(`/sign/${token}`); setDoc(res.data); setSignerName(res.data.entity_name || ""); if (res.data.signed) setStep("done"); }
    catch (e) { setError(e?.response?.data?.detail || "This link is invalid or has expired."); }
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const initiate = async () => {
    setBusy(true);
    try { const res = await api.post(`/sign/${token}/initiate`, { signer_name: signerName }); setHintOtp(res.data.sandbox_otp); setStep("otp"); toast.success("OTP sent to your email"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };
  const verify = async () => {
    setBusy(true);
    try { const res = await api.post(`/sign/${token}/verify`, { otp }); setCertId(res.data.certificate_id); setStep("done"); toast.success("Agreement signed"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Invalid OTP"); } finally { setBusy(false); }
  };

  if (error) return <Center><div className="text-center"><WarningCircle size={40} className="text-red-500 mx-auto mb-3" /><h1 className="font-heading text-2xl text-zinc-900 mb-1">Link unavailable</h1><p className="text-sm text-zinc-500">{error}</p></div></Center>;
  if (!doc) return <Center><div className="h-8 w-8 border-2 border-zinc-200 border-t-zinc-900 rounded-full animate-spin" /></Center>;

  return (
    <div className="min-h-screen bg-zinc-100 py-10 px-4">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <div className="h-9 w-9 grid place-items-center bg-zinc-950 text-white rounded-sm font-heading font-bold">L</div>
          <div><div className="font-heading tracking-tight text-zinc-900">{doc.company?.legal_name || "Digital Agreement"}</div><div className="text-[11px] uppercase tracking-widest text-zinc-500">Secure e-Sign</div></div>
        </div>

        <div className="bg-white border border-zinc-200 rounded-sm p-8 mb-4">
          <div className="flex items-center justify-between mb-4">
            <h1 className="font-heading text-2xl tracking-tight text-zinc-900">{doc.title}</h1>
            <span className="text-xs text-zinc-400">{doc.agreement_code} · v{doc.version}</span>
          </div>
          <div className="prose prose-sm max-w-none text-zinc-700 whitespace-pre-wrap text-sm leading-7 border-t border-zinc-100 pt-4">{doc.filled_body}</div>
        </div>

        {step === "done" ? (
          <div data-testid="sign-success" className="bg-white border border-emerald-200 rounded-sm p-8 text-center">
            <SealCheck size={44} weight="duotone" className="text-emerald-500 mx-auto mb-3" />
            <h2 className="font-heading text-xl text-zinc-900 mb-1">Agreement signed successfully</h2>
            <p className="text-sm text-zinc-500">A signed copy has been recorded securely.{certId ? " Your digital certificate has been issued." : ""}</p>
          </div>
        ) : step === "review" ? (
          <div className="bg-white border border-zinc-200 rounded-sm p-6">
            <Label className="text-xs text-zinc-500 mb-1.5 block">Your Full Name</Label>
            <Input data-testid="signer-name" value={signerName} onChange={(e) => setSignerName(e.target.value)} className="rounded-sm h-11 mb-4" />
            <Button data-testid="sign-initiate" onClick={initiate} disabled={busy} className="w-full h-11 rounded-sm bg-[#0047AB] hover:bg-[#003a8c]"><ShieldCheck size={18} className="mr-2" /> Proceed to e-Sign</Button>
            <p className="text-[11px] text-zinc-400 mt-3 text-center">You will receive a one-time code to authorize your signature. No Aadhaar or sensitive credentials are stored.</p>
          </div>
        ) : (
          <div className="bg-white border border-zinc-200 rounded-sm p-6">
            <Label className="text-xs text-zinc-500 mb-1.5 block">Enter the OTP</Label>
            <Input data-testid="otp-input" value={otp} onChange={(e) => setOtp(e.target.value)} className="rounded-sm h-11 mb-2 tracking-[0.4em] text-center text-lg" maxLength={6} />
            {hintOtp && <p className="text-[11px] text-amber-600 mb-3 text-center">Sandbox mode — your code is <b>{hintOtp}</b></p>}
            <Button data-testid="sign-verify" onClick={verify} disabled={busy} className="w-full h-11 rounded-sm bg-[#0047AB] hover:bg-[#003a8c]"><SealCheck size={18} className="mr-2" /> Sign Agreement</Button>
          </div>
        )}
      </div>
    </div>
  );
}

const Center = ({ children }) => <div className="min-h-screen grid place-items-center bg-zinc-100">{children}</div>;
